import React, { useMemo, useState } from "react";
import { api, mediaSrc } from "@/lib/api";
import { toast } from "sonner";

/**
 * WoAnswerSheet — pinned "answer sheet" at the top of a WO detail page.
 *
 * Four boxes, in reading order:
 *   1. COMPLAINT      – job.description (verbatim, no rewrites)
 *   2. HUMAN NOTES    – worker write-up only. Filters out any note whose
 *                       `source` marks it as a system/sync line (clickup,
 *                       agent/ProMan bot, appfolio import, tenant intake).
 *                       Also includes free-form `job.tech_notes`.
 *   3. WHAT WAS DONE  – job.completion_notes + after_photos thumbnails.
 *   4. WHO / STATUS / ADDRESS – assignee, status pill, property + unit.
 *
 * Extras below the sheet:
 *   – "System activity" (collapsed <details>): sync/agent/clickup notes so
 *     they don't muddy the human write-up but are still auditable.
 *   – When the job is a combined WO (checklist_items has source_wo_number
 *     entries), each merged child renders its own mini answer-sheet card
 *     with its own complaint / notes / done text and after-photos.
 *
 * Rules (per user spec):
 *   – Never mutate the job.
 *   – Never hide water protocol / moisture / quotes / files — those live
 *     BELOW this sheet, untouched by this component.
 *   – ClickUp + agent lines never appear in Human Notes.
 */

// A note is "system" if the source explicitly marks it as sync/bot/import,
// or if the text starts with a canonical sync prefix. We keep this list
// tight so we never silently swallow a real worker write-up.
const SYSTEM_SOURCES = new Set([
  "clickup", "agent", "system", "sync", "tenant_intake",
  "backfill", "appfolio", "ai", "bot", "proman",
]);
const SYSTEM_PREFIXES = [
  "[ClickUp",  "[clickup",  "[Sync", "[sync",
  "[AppFolio", "[appfolio", "[ProMan", "[proman",
  "[Bot",      "[bot",      "[Agent", "[agent",
  "[from ",    "[Lunch]",   "[Auto",
  "Auto-",     "auto-",
  "Imported from ", "Synced from ", "Pulled from ",
];
function isSystemNote(n) {
  if (!n) return false;
  const src = (n.source || "").toLowerCase();
  if (SYSTEM_SOURCES.has(src)) return true;
  const text = (n.text || "").trim();
  return SYSTEM_PREFIXES.some(p => text.startsWith(p));
}

const STATUS_LABEL = {
  new: "New", pool: "Pool", assigned: "Assigned",
  in_progress: "On-site", completed: "Completed",
  canceled: "Canceled", follow_up: "Follow-up", merged: "Merged",
};
const STATUS_TONE = {
  new: "bg-amber-100 text-amber-800 border-amber-300",
  pool: "bg-amber-100 text-amber-800 border-amber-300",
  assigned: "bg-orange-100 text-orange-800 border-orange-300",
  in_progress: "bg-cyan-100 text-cyan-800 border-cyan-300",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  canceled: "bg-slate-200 text-slate-600 border-slate-300",
  follow_up: "bg-purple-100 text-purple-800 border-purple-300",
  merged: "bg-slate-200 text-slate-600 border-slate-300",
};

function StatusPill({ status }) {
  const s = status || "new";
  const tone = STATUS_TONE[s] || STATUS_TONE.new;
  const label = STATUS_LABEL[s] || s;
  return (
    <span data-testid={`answer-sheet-status-${s}`}
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-black uppercase tracking-widest border rounded ${tone}`}>
      {label}
    </span>
  );
}

function Photo({ src, i, alt = "After" }) {
  if (!src) return null;
  return (
    <a href={src} target="_blank" rel="noreferrer"
      data-testid={`answer-sheet-after-photo-${i}`}
      className="block h-20 w-20 flex-shrink-0 border border-slate-300 rounded overflow-hidden bg-slate-100 hover:ring-2 hover:ring-emerald-400">
      <img src={src} alt={alt} className="h-full w-full object-cover" loading="lazy"
        onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}/>
    </a>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

/**
 * CleanupButton — small AI polish control we render inline next to the
 * "Human notes" and "What was done" section labels.
 *
 * Behaviour:
 *   – If there is nothing to clean, we still render a disabled hint so the
 *     control feels present-and-accounted-for (users complained when it
 *     silently disappeared).
 *   – If the field has already been AI-cleaned (job carries `<field>_original`),
 *     we flip to "Undo · Restore original" so the admin can revert instantly.
 *   – Every action calls the parent's `onRefresh()` so the pinned Answer
 *     Sheet reloads with the new text; we optimistically toast on success.
 */
function CleanupButton({ job, field, hasText, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const originalKey = field === "completion_notes" ? "completion_notes_precleanup" : "tech_notes_precleanup";
  const cleanedAtKey = field === "completion_notes" ? "completion_notes_cleaned_at" : "tech_notes_cleaned_at";
  const wasCleaned = Boolean(job?.[originalKey]);
  const cleanedAt = job?.[cleanedAtKey];

  const runCleanup = async () => {
    if (busy || !hasText) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/cleanup-notes`, { field });
      toast.success("Notes cleaned up ✨", {
        description: data?.cleaned_by ? `Polished by ${data.cleaned_by}` : undefined,
      });
      onRefresh && onRefresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Cleanup failed";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const runRestore = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/restore-notes`, { field });
      toast.success("Restored original notes");
      onRefresh && onRefresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Restore failed";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  if (wasCleaned) {
    return (
      <div className="flex items-center gap-2">
        <span
          className="text-[9px] font-black uppercase tracking-widest bg-cyan-100 text-cyan-800 border border-cyan-300 px-1.5 py-0.5 rounded"
          title={cleanedAt ? `Cleaned ${fmtDate(cleanedAt)}` : "AI-cleaned"}
          data-testid={`answer-sheet-cleaned-badge-${field}`}
        >
          AI-Cleaned
        </span>
        <button
          type="button"
          onClick={runRestore}
          disabled={busy}
          data-testid={`answer-sheet-restore-${field}`}
          className="text-[10px] font-black uppercase tracking-widest text-slate-600 hover:text-slate-900 underline decoration-dotted disabled:opacity-50"
        >
          {busy ? "Restoring…" : "Undo"}
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={runCleanup}
      disabled={busy || !hasText}
      data-testid={`answer-sheet-cleanup-${field}`}
      title={hasText ? "AI-polish this text (fixes punctuation, splits run-ons)" : "Nothing to clean yet"}
      className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-cyan-800 bg-cyan-50 border border-cyan-300 hover:bg-cyan-100 disabled:opacity-40 disabled:cursor-not-allowed px-2 py-0.5 rounded"
    >
      <span aria-hidden>✨</span>
      {busy ? "Cleaning…" : "Clean up"}
    </button>
  );
}

/**
 * Prose — renders worker text with sensible spacing so long unbroken
 * paragraphs don't come across as a wall of text. Splits on double
 * newlines first, then on sentence boundaries when a paragraph is very
 * long (>160 chars). Never rewrites the words — only visual sugar.
 */
function Prose({ text, testid }) {
  const paragraphs = useMemo(() => {
    const raw = (text || "").trim();
    if (!raw) return [];
    // First break on explicit newlines (worker used enter key)
    let paras = raw.split(/\n\s*\n+|\n/).map(p => p.trim()).filter(Boolean);
    // Then for any paragraph over 160 chars, split at sentence endings so
    // long dictation-style paragraphs get a natural breather.
    paras = paras.flatMap(p => {
      if (p.length < 160) return [p];
      // Keep the terminator with the sentence.
      const sentences = p.match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g) || [p];
      return sentences.map(s => s.trim()).filter(Boolean);
    });
    return paras;
  }, [text]);

  if (paragraphs.length === 0) return null;
  return (
    <div data-testid={testid} className="text-[15px] leading-relaxed text-slate-800 space-y-2">
      {paragraphs.map((p, i) => (
        <p key={i} className="whitespace-pre-wrap">{p}</p>
      ))}
    </div>
  );
}

function Box({ label, testid, children, tone = "slate", action = null }) {
  const toneMap = {
    slate: "border-slate-300 bg-slate-50",
    amber: "border-amber-300 bg-amber-50",
    cyan: "border-cyan-300 bg-cyan-50",
    emerald: "border-emerald-300 bg-emerald-50",
  };
  return (
    <div className={`border-l-4 ${toneMap[tone]} p-3 rounded-r`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex-1">{label}</div>
        {action}
      </div>
      <div className="text-sm text-slate-900 leading-snug">{children}</div>
    </div>
  );
}

function MergedChildCard({ item }) {
  const after = (item.after_photos || []).map(p => mediaSrc(p)).filter(Boolean);
  return (
    <div className="border border-slate-300 bg-white p-3 rounded"
      data-testid={`answer-sheet-child-${item.id}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          {item.source_wo_number ? `WO #${item.source_wo_number}` :
           item.source_appfolio_id ? `AF #${item.source_appfolio_id}` :
           "Line"}
        </span>
        {item.life_safety && (
          <span className="text-[9px] font-black uppercase tracking-widest bg-red-600 text-white px-1.5 py-0.5 rounded">
            Life-Safety
          </span>
        )}
        {item.done ? (
          <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white px-1.5 py-0.5 rounded">
            Done · {fmtDate(item.done_at)}
          </span>
        ) : (
          <span className="text-[9px] font-black uppercase tracking-widest bg-amber-500 text-white px-1.5 py-0.5 rounded">
            Open
          </span>
        )}
        <div className="text-sm font-black text-slate-900 truncate">{item.text}</div>
      </div>
      <div className="space-y-2">
        <Box label="Complaint" tone="amber" testid={`answer-sheet-child-complaint-${item.id}`}>
          <Prose text={item.description || item.text || "—"}/>
        </Box>
        <Box label="What was done" tone="emerald" testid={`answer-sheet-child-done-${item.id}`}>
          {(item.notes || "").trim()
            ? <Prose text={item.notes}/>
            : <div className="text-slate-400 italic">{item.done ? "Marked done." : "—"}</div>}
          {after.length > 0 && (
            <div className="mt-3 flex gap-2 flex-wrap">
              {after.map((src, i) => <Photo key={i} src={src} i={i}/>)}
            </div>
          )}
        </Box>
      </div>
    </div>
  );
}

export default function WoAnswerSheet({ job, onRefresh }) {
  const notes = Array.isArray(job?.notes) ? job.notes : [];
  const humanNotes = useMemo(() => notes.filter(n => !isSystemNote(n)), [notes]);
  const systemNotes = useMemo(() => notes.filter(n => isSystemNote(n)),  [notes]);
  const [showSystem, setShowSystem] = useState(false);

  const afterPhotos = useMemo(() => {
    // Use the shared mediaSrc() so ClickUp/CDN photos with a `url` field AND
    // locally-uploaded photos with a `path` field both resolve to a working
    // browser src. Previously we handled path/url manually and every
    // ClickUp-synced photo rendered a broken image.
    return (job?.after_photos || []).map(p => mediaSrc(p)).filter(Boolean);
  }, [job?.after_photos]);

  // Combined WO detection — parent carries children in checklist_items with
  // source_wo_number/id set. Fall back gracefully if the field isn't there.
  const mergedChildren = useMemo(() => {
    return (job?.checklist_items || []).filter(x => x.source_wo_number || x.source_appfolio_id);
  }, [job?.checklist_items]);

  // All hooks above — safe to bail now.
  if (!job) return null;

  const doneText = (job.completion_notes || "").trim();
  const techNotesText = (job.tech_notes || "").trim();
  const isCombined = mergedChildren.length > 0;

  const woLabel = job.work_order_number ? `#${job.work_order_number}` : (job.appfolio_wo_id ? `AF#${job.appfolio_wo_id}` : "WO");
  const addr = [job.property_address || "", job.unit && `Unit ${job.unit}`].filter(Boolean).join(" · ");
  const assignee = job.assigned_to_name || job.company_name || "Unassigned";

  return (
    <div className="bg-white border-2 border-slate-900 rounded-lg overflow-hidden shadow-md mb-4"
      data-testid="wo-answer-sheet">
      <div className="flex items-center gap-2 bg-slate-900 text-white px-3 py-2 flex-wrap">
        <span className="text-[10px] font-black uppercase tracking-widest bg-cyan-400 text-slate-900 px-2 py-0.5 rounded">Answer Sheet</span>
        <span className="text-[11px] font-black tabular-nums">{woLabel}</span>
        {job.appfolio_wo_id && job.work_order_number && (
          <span className="text-[9px] font-mono text-white/60">· AF# {job.appfolio_wo_id}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <StatusPill status={job.status}/>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* WHO / STATUS / ADDRESS row — glanceable */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
          <div data-testid="answer-sheet-address" className="min-w-0">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Address</div>
            <div className="font-bold text-slate-900 truncate">{addr || "—"}</div>
          </div>
          <div data-testid="answer-sheet-assignee" className="min-w-0">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Assigned to</div>
            <div className="font-bold text-slate-900 truncate">{assignee}</div>
          </div>
          <div data-testid="answer-sheet-timing" className="min-w-0">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Timing</div>
            <div className="font-bold text-slate-900 truncate">
              {job.completed_at ? `Closed ${fmtDate(job.completed_at)}` :
               job.clock_in_at ? `On-site since ${fmtDate(job.clock_in_at)}` :
               job.scheduled_for ? `Scheduled ${fmtDate(job.scheduled_for)}` :
               job.created_at ? `Opened ${fmtDate(job.created_at)}` : "—"}
            </div>
          </div>
        </div>

        {isCombined ? (
          <>
            <Box label="Combined WO — see each line below" tone="cyan" testid="answer-sheet-combined-banner">
              This ticket is a combined WO with {mergedChildren.length} merged {mergedChildren.length === 1 ? "line" : "lines"}. Each keeps its own complaint / notes / done.
            </Box>
            <div className="space-y-2">
              {mergedChildren.map(child => <MergedChildCard key={child.id} item={child}/>)}
            </div>
          </>
        ) : (
          <>
            {/* 1. COMPLAINT */}
            <Box label="Complaint" tone="amber" testid="answer-sheet-complaint">
              {(job.description || "").trim()
                ? <Prose text={job.description}/>
                : <div className="text-slate-400 italic">—</div>}
            </Box>

            {/* 2. HUMAN NOTES */}
            <Box label={`Human notes${humanNotes.length ? ` · ${humanNotes.length}` : ""}`}
              tone="slate" testid="answer-sheet-human-notes"
              action={
                <CleanupButton
                  job={job}
                  field="tech_notes"
                  hasText={Boolean(techNotesText)}
                  onRefresh={onRefresh}
                />
              }>
              {(humanNotes.length === 0 && !techNotesText) ? (
                <div className="text-slate-400 italic">No worker notes yet.</div>
              ) : (
                <ul className="space-y-1.5">
                  {techNotesText && (
                    <li className="border-l-2 border-cyan-500 pl-3 py-1" data-testid="answer-sheet-tech-notes">
                      <Prose text={techNotesText}/>
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-1">
                        Tech notes
                      </div>
                    </li>
                  )}
                  {humanNotes.slice().reverse().map((n, i) => (
                    <li key={n.id || i}
                      data-testid={`answer-sheet-human-note-${i}`}
                      className="border-l-2 border-slate-400 pl-3 py-1">
                      <Prose text={n.text}/>
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-1">
                        {n.by_name || "Field"}{n.at ? ` · ${fmtDate(n.at)}` : ""}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Box>

            {/* 3. WHAT WAS DONE */}
            <Box label="What was done" tone="emerald" testid="answer-sheet-done"
              action={
                <CleanupButton
                  job={job}
                  field="completion_notes"
                  hasText={Boolean(doneText)}
                  onRefresh={onRefresh}
                />
              }>
              {doneText ? (
                <Prose text={doneText}/>
              ) : job.status === "completed" ? (
                <div className="text-slate-500 italic">Marked completed without a close-out note.</div>
              ) : (
                <div className="text-slate-400 italic">Not closed yet.</div>
              )}
              {afterPhotos.length > 0 && (
                <div className="mt-3 flex gap-2 flex-wrap" data-testid="answer-sheet-after-photos">
                  {afterPhotos.slice(0, 12).map((src, i) => <Photo key={i} src={src} i={i}/>)}
                  {afterPhotos.length > 12 && (
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 self-center pl-1">
                      +{afterPhotos.length - 12} more below
                    </span>
                  )}
                </div>
              )}
            </Box>
          </>
        )}

        {/* System activity — collapsed by default */}
        {systemNotes.length > 0 && (
          <details className="border border-dashed border-slate-300 rounded"
            open={showSystem}
            onToggle={(e) => setShowSystem(e.currentTarget.open)}
            data-testid="answer-sheet-system-activity">
            <summary className="cursor-pointer px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 select-none">
              System activity · {systemNotes.length}
            </summary>
            <ul className="px-3 pb-2 space-y-1.5">
              {systemNotes.slice().reverse().map((n, i) => (
                <li key={n.id || i}
                  data-testid={`answer-sheet-system-note-${i}`}
                  className="text-xs text-slate-600 border-l-2 border-slate-300 pl-2 whitespace-pre-wrap">
                  {n.text}
                  <span className="ml-1 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    · {(n.source || "sync")}{n.at ? ` · ${fmtDate(n.at)}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}
